import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {FormContext} from '../form';
import {FORM_STEP_KEY, FORM_STEP_ORDER} from '../../../constant/FormStep';
import {KEY_MAP_KEY} from '../../../constant/KeyMap';
import ApiService from '../../../service/ApiService';
import useSubmit from './useSubmit';

const [firstStep] = FORM_STEP_ORDER;
const [lastStep] = [...FORM_STEP_ORDER].reverse();

function useFormStep(params) {
  const {hooks} = params;
  const [stepErrors, setStepErrors] = useState({});
  const [stepFields, setStepFields] = useState({});
  const [stepValues, setStepValues] = useState({});
  const [isStepSubmitted, setIsStepSubmitted] = useState(false);
  const [isStepValid, setIsStepValid] = useState(true);
  const {formValues, goToNextStep, onSkipStep, registerStepApi, step, updateValues} = useContext(FormContext);
  const {onSubmit, requestStatus, submissionErrors} = useSubmit({formValues});

  const validate = useCallback((formStepFields, formStepValues) => {
    const validationErrors = Object.entries(formStepFields).reduce((acc, [name, validation]) => {
      const validationRules = Array.isArray(validation) ? validation : [validation];

      validationRules.forEach(({isRequired, validationFn}) => {
        if (isRequired) {
          const formValue = formStepValues[name];
          const errors = validationFn(formValue, formStepValues);

          if (errors) {
            acc[name] = [
              ...acc[name] || [],
              ...errors
            ];
          }
        }
      });

      return acc;
    }, {});

    if (Object.keys(validationErrors).length === 0) {
      return null;
    }

    return validationErrors;
  }, []);

  useEffect(() => {
    const validationErrors = validate(stepFields, stepValues);

    if (validationErrors) {
      setStepErrors(prevState => {
        const nextState = {
          ...prevState,
          ...validationErrors
        };

        return nextState;
      });
    } else {
      setStepErrors({});
    }

    setIsStepValid(!validationErrors);
  }, [stepFields, stepValues, validate]);

  const registerField = useCallback(fieldProperties => {
    const {defaultValue, name, validation} = fieldProperties;
    let initValue = null;
    if (formValues[name] === undefined) {
      initValue = defaultValue;
    } else {
      initValue = formValues[name];
    }

    setStepFields(prevState => {
      const nextState = {
        ...prevState,
        [name] : validation
      };

      return nextState;
    });

    setStepValues(prevState => {
      const nextState = {
        ...prevState,
        [name] : initValue
      };

      return nextState;
    });
  }, [formValues]);

  const unregisterField = useCallback(fieldProperties => {
    const {name} = fieldProperties;

    setStepFields(prevState => {
      const nextState = {
        ...prevState,
      };

      delete nextState[name];

      return nextState;
    });

    setStepValues(prevState => {
      const nextState = {
        ...prevState
      };

      delete nextState[name];

      return nextState;
    });
  }, []);

  const onChange = useCallback((propertyName, value) => {
    setStepValues(prevState => {
      const nextState = {
        ...prevState,
        [propertyName] : value
      };

      return nextState;
    });
  }, []);

  /*
    The step is reported as left behind, so this runs on the paths that actually leave it
    -- a valid advance, a skip, and the submit -- and on no other. It used to run first
    thing in handleOnGoNext, before validation, which reported an attempt rather than an
    advance: one mistyped email address produced eleven form_step events at the same
    index while the form never moved, and a funnel cannot tell those from real traffic.
  */
  const reportStepAdvance = useCallback(() => {
    if (typeof hooks?.onGoNext === 'function') {
      hooks.onGoNext(step, stepValues, formValues);
    }
  }, [formValues, hooks, step, stepValues]);

  const handleOnGoNext = useCallback(async () => {
    if (step === FORM_STEP_KEY.SUBMIT) {
      reportStepAdvance();

      onSubmit();
    } else {
      setIsStepSubmitted(true);

      if (isStepValid) {
        reportStepAdvance();

        updateValues(stepValues);

        setStepFields({});
        setStepValues({});
        setStepErrors({});
        setIsStepValid(true);
        setIsStepSubmitted(false);

        await ApiService.delay(100);

        goToNextStep();
      }
    }
  }, [
    goToNextStep,
    isStepValid,
    onSubmit,
    reportStepAdvance,
    step,
    stepValues,
    updateValues
  ]);

  /*
    Lets the breadcrumb control (rendered outside this step's context, see useForm's
    advanceActiveStep) leave the step through this exact function instead of its own
    unvalidated shortcut -- see R11.
  */
  useEffect(() => {
    if (typeof registerStepApi === 'function') {
      registerStepApi({handleOnGoNext, isStepValid});
    }
  }, [handleOnGoNext, isStepValid, registerStepApi]);

  /*
    Skipping is still leaving the step behind. Wired straight to the form's goToNextStep
    before, which walked past the only optional step -- customerPhoneNumber, index 9 --
    without reporting it, leaving a phantom drop between 8 and 10 in the funnel.
  */
  const handleOnSkipStep = useCallback(() => {
    reportStepAdvance();

    onSkipStep();
  }, [onSkipStep, reportStepAdvance]);

  const getFieldErrors = useCallback(name => {
    const fieldErrors = stepErrors[name];

    return fieldErrors;
  }, [stepErrors]);

  const getFieldValue = useCallback(name => {
    const stepValue = stepValues[name];

    return stepValue;

  }, [formValues, stepValues]);

  const handlers = {
    [KEY_MAP_KEY.ENTER] : handleOnGoNext
  };

  const stepCount = useMemo(() => {
    if (step !== firstStep && step !== lastStep) {
      return FORM_STEP_ORDER.indexOf(step);
    }

    return null;
  }, [step])

  const formStepContext = {
    getFieldErrors,
    getFieldValue,
    handleOnGoNext,
    handleOnSkipStep,
    isStepSubmitted,
    isStepValid,
    onChange,
    onSubmit,
    registerField,
    requestStatus,
    stepCount,
    stepErrors,
    submissionErrors,
    unregisterField
  };

  return {
    formStepContext,
    handlers
  };
}

export default useFormStep;
